/**
 * Scene-copy session state — which scene is currently armed in the
 * OP-AbletonSceneCopy device's clipboard.
 *
 * WHY THIS IS SESSION STATE AND NOT PER-ITEM. The clipboard belongs to the
 * DEVICE, not to a button: there is exactly one armed row in Live at any
 * moment. Two SceneCopy items on the same page (or the same item on two
 * paired devices) are two views of that one clipboard, so the armed slot
 * lives here and every item lights it — the identical relationship
 * `transpose/session.ts` has with Transpose and Capo rows.
 *
 * ⚠️ THIS IS AN OPTIMISTIC MIRROR, NOT A DEVICE READBACK. The device does
 * emit its real clipboard state as `/OP-AbletonSceneCopy/armed <srcIdx>
 * <nclips>` on UDP 11204, but OPButtons has no inbound path for that OSC
 * stream today — the PWA only ever SENDS to the device. So this signal
 * records what we last *asked* for, and can be wrong in exactly the cases
 * you would expect:
 *
 *   - the operator armed a scene from the device's own panel in Live,
 *   - a copy was rejected (`/error no_scene_selected`, `bad_scene_index`),
 *   - the device wasn't running when we sent.
 *
 * It is still worth having: without it the COPY column gives no feedback at
 * all, and a wrong highlight is recoverable by pressing COPY again while a
 * missing one leaves the operator guessing mid-set. If an inbound OSC path
 * ever lands, this is the single place to swap the source — make `armedScene`
 * read from the bridge feed and delete `noteArmed`, and every item follows.
 *
 * NOT persisted, deliberately. Live's clipboard does not survive a Live
 * restart, so restoring a stale "scene 3 armed" highlight on PWA reload
 * would be a confident lie about the device's state.
 */

import { createSignal } from 'solid-js';

/** 1-based Live scene number currently believed to be armed, or null when
 *  nothing has been copied this session. 1-based because every human-facing
 *  number in this feature is — the 0-based wire conversion happens once, in
 *  `sendSceneCopy`. */
const [armedSceneSig, setArmedSceneSig] = createSignal<number | null>(null);

export const armedScene = armedSceneSig;

/** Record that we asked the device to arm `scene` (1-based). Called by the
 *  COPY column after a successful-looking dispatch. See the optimistic-mirror
 *  caveat in this file's header. */
export function noteArmed(scene: number): void {
  setArmedSceneSig(scene);
}

/**
 * Forget the armed slot. Called after a PASTE (operator ask 2026-08-30): the
 * highlight means "copied, waiting to be placed", so once it has been placed
 * there is nothing pending and a cell left lit from the last move is just
 * noise on a stage surface.
 *
 * ⚠️ THIS DOES NOT DISARM THE DEVICE, and it cannot. The device's inbound
 * wire has no clear/disarm verb at all - copy, copy_scene, paste,
 * paste_scene, copy_to, mode, sethost, setport, rescan, status is the
 * complete list - so its clipboard survives a paste by design (that is what
 * makes "copy once, paste into three scenes" work). After this runs, the UI
 * shows nothing armed while the device still holds the row, so a further
 * PASTE will still paste it.
 *
 * That gap is deliberate and bounded rather than hidden: the two-tap guard,
 * ON by default for this kind, is what actually protects against a stray
 * press. If an inbound OSC path ever lands, `/armed <src> <n>` becomes the
 * source of truth and this whole optimistic layer goes away.
 */
export function clearArmed(): void {
  setArmedSceneSig(null);
}
